import type { FC } from 'react';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useContentTypesTitleUpdate } from '@confluence/content-types-header/entry-points/useContentTypesTitleUpdate';
import { fg } from '@confluence/feature-gating';
import { useSpaceId } from '@confluence/space-utils';
import { KeyboardEventKeys } from '@confluence/keyboard-events';
import { Attribution, ErrorBoundary, ErrorDisplay } from '@confluence/error-boundary';
import {
	useRouteActions,
	useRouteDataRef,
} from '@confluence/route-manager/entry-points/RouteState';
import { generateSlug } from '@confluence/route-manager/entry-points/generateSlug';
import { VIEW_PAGE } from '@confluence/named-routes';
import { useOptimisticTitleChange } from '@confluence/page-tree-refresh-state-container/entry-points/useOptimisticTitleChange';
import type { FullPageEditorPresetAPI } from '@confluence/editor-presets/entry-points/useFullPageEditorPreset';

import { LivePagesTitlePresentation } from './LivePagesTitlePresentation';
import { MAX_LENGTH_OF_TITLE, AI_SUGGEST_TITLE_PROPS } from './EditorTitle';

const i18n = defineMessages({
	placeholder: {
		id: 'editor-title.live-pages-title.placeholder',
		defaultMessage: 'Give this page a title',
		description: 'placeholder prompting user to populate the title field on live pages',
	},
	duplicateTitleError: {
		id: 'editor-title.live-pages-title.duplicate-title-error',
		defaultMessage:
			'There’s already a page named “{pageTitle}” in this space. Give this page a unique title.',
		description: 'error to be shown in popup if there is duplicate title of same content type',
	},
	invalidTitleError: {
		id: 'editor-title.live-pages-title.empty-title-error',
		defaultMessage: 'Give your page a unique title so you can find it easily later',
		description: 'error to be shown in popup if the title is empty or comprised of only whitespace',
	},
	generalError: {
		id: 'editor-title.live-pages-title.general-error',
		defaultMessage: 'An error occurred on our end. Refresh the page and try again.',
		description: 'general error to be shown in popup if the title update request fails',
	},
});

export type LivePagesTitleProps = {
	title: string;
	contentId: string;
	isDisabled?: boolean;
	focusEditor?: () => void;
	onFocusChanged?: (focusChanged: boolean) => void;
	onTitleBlur?: () => void;
	viewMode?: boolean;
	isTitleCenterAligned?: boolean;
	version?: number;
	editorApi?: FullPageEditorPresetAPI;
};

export const LivePagesTitle: FC<LivePagesTitleProps> = ({
	title,
	contentId,
	isDisabled,
	focusEditor,
	onFocusChanged,
	onTitleBlur,
	viewMode,
	isTitleCenterAligned,
	version = 1,
	editorApi,
}) => {
	const { formatMessage } = useIntl();
	const spaceId = useSpaceId();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const textAreaRef = useRef<HTMLTextAreaElement>(null);
	const hasEditEventFiredRef = useRef<boolean>(false);
	const { updateError, updateTitle, updateLoading } = useContentTypesTitleUpdate(contentId, title);
	const { setOptimisticTitle } = useOptimisticTitleChange(contentId, title);
	const [isUserEditing, setUserIsEditing] = useState<boolean>(false);

	const { replace } = useRouteActions();
	const routeDataRef = useRouteDataRef();

	const onTitleUpdate = useCallback(
		async (value: string) => {
			setOptimisticTitle(value);
			// We don't want to update the title if the field is blank, otherwise it will update to a default title
			if (!getIsTitleEmpty(value)) {
				await updateTitle(value);

				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'updated',
						actionSubject: 'title',
						source: 'editorTitle',
						attributes: { isLivePage: true },
						objectId: contentId,
						objectType: 'page',
						containerType: 'space',
						containerId: spaceId,
					},
				}).fire();

				// We want to fire this event only once to indicate the editor has been edited; the first event measures all title updates.
				if (!hasEditEventFiredRef.current) {
					createAnalyticsEvent({
						type: 'sendTrackEvent',
						data: {
							source: 'editorTitle',
							action: 'edited',
							actionSubject: 'editor',
							objectId: contentId,
							objectType: 'page',
							attributes: { isLivePage: true },
							containerType: 'space',
							containerId: spaceId,
						},
					}).fire();
					// Mark the event as fired
					hasEditEventFiredRef.current = true;
				}
			}
		},
		[contentId, createAnalyticsEvent, spaceId, updateTitle, setOptimisticTitle],
	);

	useEffect(() => {
		const contentSlug = generateSlug(title);
		if (
			routeDataRef.current &&
			contentSlug &&
			contentSlug !== routeDataRef.current.routeParams?.contentSlug &&
			routeDataRef.current.routeName === VIEW_PAGE.name
		) {
			// Construct the new URL with the updated slug and existing query params and any hash that was present
			const newUrl = VIEW_PAGE.toUrl(
				{
					...routeDataRef.current.routeParams,
					contentSlug,
				},
				{
					query: {
						...(routeDataRef.current.queryParams || {}),
					},
					hash: routeDataRef.current.hash,
				},
			);

			replace(`${newUrl}`, false);
		}
	}, [title, replace, routeDataRef]);

	const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
		// If the user is composing characters using, e.g., the Hiragana - Kana keyboard,
		// short circuit the code to prevent the bug in Chrome where the selected text
		// gets copied into the editor body.
		if (event.nativeEvent.isComposing) {
			return;
		}

		const isTabPressed = event.key === KeyboardEventKeys.TAB.key && !event.shiftKey;
		const isEnterPressed = event.key === KeyboardEventKeys.ENTER.key;

		if (isTabPressed || isEnterPressed) {
			event.preventDefault();
			event.stopPropagation();

			if (focusEditor) {
				if (editorApi && fg('confluence_frontend_page_title_enter_improvements')) {
					if (isEnterPressed) {
						editorApi?.core?.actions.execute(
							editorApi?.pageTitleUtils?.commands?.insertNewLineAtDocStart(),
						);
					}
					if (isTabPressed) {
						editorApi?.core?.actions.execute(editorApi?.pageTitleUtils?.commands?.selectDocStart());
					}
				}
				// if we trigger focusEditor, it will blur the title field.
				// The title is saved on blur, calling onTitleUpdate is avoided here to prevent double trigger.
				focusEditor();
			} else {
				// if we don't have focusEditor prop, we should call onTitleUpdate
				void onTitleUpdate(event.currentTarget.value);
				setUserIsEditing(false);
			}
		}
	};

	const handleFocus = () => onFocusChanged?.(true);
	const handleBlur = async (e: React.FocusEvent<HTMLTextAreaElement>) => {
		onTitleBlur?.();
		onFocusChanged?.(false);
		setUserIsEditing(false);
		void onTitleUpdate(e.target.value);
	};

	const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>, fieldProps: any) => {
		fieldProps.onChange(e);
		adjustTextAreaHeight(textAreaRef);
		setUserIsEditing(true);
	};

	useEffect(() => {
		// To address a timing issue where the height adjustment was occurring before the new title was rendered,
		// a delay  was implemented. Ensures the new title is fully rendered before the text area height is adjusted
		setTimeout(() => {
			adjustTextAreaHeight(textAreaRef);
		}, 0);
	}, [title]);

	useLayoutEffect(() => {
		adjustTextAreaHeight(textAreaRef);
	}, []);

	useEffect(() => {
		if (
			isDisabled ||
			viewMode ||
			version !== 1 ||
			!getIsTitleEmpty(title) ||
			!textAreaRef.current
		) {
			return;
		}

		// When its a new live page with an empty title, focus on the title
		textAreaRef.current.focus();
	}, [isDisabled, title, version, viewMode]);

	// Used in onTitleUpdate for checking whether title update should be send to backend
	const getIsTitleEmpty = (title: string | undefined) => !title || title.trim().length === 0;

	// Title is invalid when:
	// 1. Verion is not 1 and it's empty (title was updated to be empty) OR
	// 2. It only contains empty space or tabs
	const getIsInvalidTitle = (title: string | undefined): boolean => {
		return !!((version !== 1 && !title) || (title && title.trim().length === 0));
	};

	const buildErrorMessage = (fieldProps, error) => {
		const updateErrorMessage = updateError?.message.toLowerCase();
		if (fieldProps.isInvalid) {
			return error;
		} else if (getIsInvalidTitle(fieldProps?.value)) {
			return formatMessage(i18n.invalidTitleError);
		} else if (updateErrorMessage?.includes('exists with the same title in this space')) {
			return formatMessage(i18n.duplicateTitleError, {
				pageTitle: fieldProps?.value,
			});
		} else {
			return formatMessage(i18n.generalError);
		}
	};

	const adjustTextAreaHeight = (textAreaRef: React.RefObject<HTMLTextAreaElement>) => {
		const textArea = textAreaRef.current;
		if (textArea) {
			// Reset the height in case the new content is smaller than the old content
			textArea.style.height = 'auto';
			// In SSR environment, the previous line does not cause a reflow, so the `scrollHeight` stays zero
			if (!process.env.REACT_SSR) {
				// Set the height to match the scroll height, which is the total height of the content
				textArea.style.height = `${textArea.scrollHeight}px`;
			}
		}
	};

	return (
		<ErrorBoundary attribution={Attribution.PAGE_MODES}>
			{updateError && <ErrorDisplay error={updateError} />}
			<LivePagesTitlePresentation
				title={title}
				isDisabled={isDisabled}
				viewMode={viewMode}
				isTitleCenterAligned={isTitleCenterAligned}
				isUserEditing={isUserEditing}
				updateLoading={updateLoading}
				updateError={updateError}
				handleBlur={handleBlur}
				handleFocus={handleFocus}
				handleChange={handleChange}
				handleKeyDown={handleKeyDown}
				adjustTextAreaHeight={adjustTextAreaHeight}
				getIsInvalidTitle={getIsInvalidTitle}
				buildErrorMessage={buildErrorMessage}
				textAreaRef={textAreaRef}
				formatMessage={formatMessage}
				i18n={i18n}
				AI_SUGGEST_TITLE_PROPS={AI_SUGGEST_TITLE_PROPS}
				maxLength={MAX_LENGTH_OF_TITLE}
			/>
		</ErrorBoundary>
	);
};
